/**
 * CORE-1197 — что разрешено гостю, вошедшему по ссылке.
 *
 * Правило вынесено сюда по той же причине, что и его серверный двойник
 * (`GuestPublishPolicy` в бэкенде): ответ нужен в трёх местах экрана — право
 * вещать, право показать камеру и ключ перезахода, — и три инлайновые копии
 * разъехались бы. Разъехавшаяся копия здесь означает микрофон, открытый над
 * subscriber-соединением: кнопка есть, SFU клиента не слушает, комната молчит.
 * Ровно этот дефект разбирался в CORE-1191, и повторять его не хочется.
 *
 * Клиент здесь ничего не РЕШАЕТ — он складывает два ответа сервера. Право
 * публиковать приходит вместе с RTC-токеном, и опередить сервер невозможно:
 * можно только соврать пользователю кнопкой.
 */

export interface GuestPublishInput {
    /**
     * `guestCanPublish` из ответа `/spectate`: комната пускает гостя в эфир
     * (жива, не «для подписчиков», есть место, открыто хоть одно устройство).
     * CORE-1859/1860 — личного «слова» больше нет: гость включается сам, как
     * участник, по общим разрешениям. `undefined` — стенд ещё
     * без серверной половины (CORE-1199); тогда гость остаётся зрителем, как и
     * до этой работы.
     */
    roomAllowsGuests: boolean | undefined;
    /**
     * `areCamerasAllowed` из ответа `/spectate`. RTC-токен не умеет разделять
     * аудио и видео (CORE-855, одно право на оба потока), поэтому запрет камер
     * исполняется на клиенте — так же, как он исполняется для обычного
     * участника, у которого токен ровно так же не разделён.
     */
    camerasAllowed: boolean | undefined;
    /**
     * CORE-1735 slice 12 / CORE-1766 — `areMicsAllowed` из ответа `/spectate` и
     * кадра `ParticipantDevicePolicyChanged`: общее разрешение включать микрофон
     * самому. `undefined` — старый сервер без поля, запрета нет.
     */
    micsAllowed?: boolean | undefined;
}

export interface GuestPublishGate {
    /** Микрофон гостя может выйти на провод. */
    mayPublish: boolean;
    /** Камера гостя может выйти на провод. */
    mayPublishVideo: boolean;
    /**
     * CORE-1766 — ведущий запретил участникам включать микрофон: кнопка не
     * включает его, но право вещать (`mayPublish`, роль токена) не меняется —
     * работающий микрофон по контракту не гаснет, его гасит разовая команда.
     */
    micBlocked: boolean;
    /**
     * Ключ перезахода в RTC. Роль читается ТОЛЬКО при join, поэтому смена права
     * обязана перезайти — иначе выданное слово оставит гостя с subscriber-
     * токеном и молчащим микрофоном под включённой кнопкой.
     */
    rejoinKey: string;
}

export function resolveGuestPublishGate(input: GuestPublishInput): GuestPublishGate {
    const mayPublish = input.roomAllowsGuests ?? false;
    return {
        mayPublish,
        // Камера — конъюнкция: без права вещать её нет вовсе, а с правом она
        // ещё и упирается в настройку комнаты.
        mayPublishVideo: mayPublish && (input.camerasAllowed ?? false),
        micBlocked: mayPublish && input.micsAllowed === false,
        rejoinKey: `guest-${mayPublish ? 'publisher' : 'subscriber'}`,
    };
}

/**
 * CORE-1197 (возврат QA 06.09.2026) — гость держит слово, а токен пришёл
 * subscriber'ом.
 *
 * Гейт выше складывает то, что сказал сервер ДО перезахода: политику комнаты и
 * выданное слово. Ответ на вопрос «а дали ли право на самом деле» приходит
 * позже и отдельно — ролью в RTC-токене, — и до сих пор его не сверял никто.
 * Расхождение молчит в обе стороны: на Agora `toggleMic` выходит по
 * `role !== 'publisher'` и кнопка просто не срабатывает, на LiveKit кнопка
 * загорается, а комната остаётся немой. Снаружи оба случая выглядят одинаково —
 * «гостю дали слово, микрофон не включается», и различить их по видеозаписи
 * нельзя (прямая жалоба QA к возврату 06.09).
 *
 * Расхождение не гипотетическое. `GrantSpeaking` в хабе пишет и объявляет слово,
 * НЕ спрашивая про место в комнате, а выдача токена заканчивается
 * `HasRoomForGuestParticipantAsync`. В комнате, упёршейся в `maxParticipants`,
 * ведущий выдаёт слово, все поверхности показывают «со словом», а токен молча
 * остаётся subscriber'ом. Ровно тот класс дефекта, ради которого гейт и вынесен
 * в отдельный модуль (CORE-1191): кнопка, не подкреплённая правами.
 *
 * Здесь НЕ гасится `mayPublish`: именно он двигает `rejoinKey`, и погасив его,
 * мы бы отменили тот самый перезаход, который и должен принести
 * publisher-токен. Это отдельный, ПОСЛЕДУЮЩИЙ ответ — «мы попросили, и нам
 * отказали».
 */
export interface GuestPublishDeniedInput {
    /** Право по гейту выше: гость считает, что говорить может. */
    mayPublish: boolean;
    /**
     * Роль, с которой RTC-слой реально подключился. `null` — ещё не знаем
     * (токен не приехал, соединение не поднялось); молчание не отказ.
     */
    tokenRole: 'publisher' | 'subscriber' | null;
}

/**
 * Сервер отказал в публикации гостю, который по гейту её ждёт.
 *
 * `false`, пока роль неизвестна: до ответа сервера утверждать нечего, а ложный
 * отказ на секунду соединения — это мигающая подсказка на ровном месте.
 */
export function isGuestPublishDenied(input: GuestPublishDeniedInput): boolean {
    return input.mayPublish && input.tokenRole === 'subscriber';
}

/**
 * CORE-1197 (возврат QA 08.09.2026) — политика гостя, как её читает клиент из
 * ответа `/spectate`.
 *
 * До этого чтение стояло инлайном в трёх местах — вход в комнату на вебе, вход
 * на нативе, а теперь ещё green room и пересверка после перезахода, — и это
 * ровно тот случай, ради которого гейт выше вынесен в модуль: копии
 * расходятся. Здесь одно правило на всех:
 *
 *  - `canPublish` истинен ТОЛЬКО по явному `true`: стенд без CORE-1199 отвечает
 *    без поля, и гость обязан остаться зрителем, а не получить кнопку над
 *    subscriber-соединением;
 *  - `camerasAllowed` ложен ТОЛЬКО по явному `false`: отсутствие поля значит
 *    «камеры разрешены», как у обычного участника (`areCamerasAllowed !== false`
 *    в экране), иначе старый стенд гасил бы камеру гостю, которому она
 *    положена.
 */
export interface GuestPolicy {
    /** Выдадут ли этому гостю publisher-токен, если он его попросит. */
    canPublish: boolean;
    /** Разрешены ли в комнате камеры. */
    camerasAllowed: boolean;
    /**
     * CORE-1859 / CORE-1860 (QA FAIL 30.10) — «Камера после подтверждения»: ведущий
     * этому гостю камеру ещё не разрешил. Есть ТОЛЬКО при явном `true`
     * от сервера; отсутствие — «не ждёт».
     */
    cameraAwaitingApproval?: boolean;
}

export type GuestPolicySource = {
    guestCanPublish?: boolean;
    areCamerasAllowed?: boolean;
    isCameraBlocked?: boolean;
};

export function resolveGuestPolicy(data: GuestPolicySource): GuestPolicy {
    return {
        canPublish: data.guestCanPublish === true,
        camerasAllowed: data.areCamerasAllowed !== false,
        ...(data.isCameraBlocked === true ? { cameraAwaitingApproval: true } : {}),
    };
}

/**
 * CORE-1859 / CORE-1860 (QA FAIL 30.10, staging 1005026/1005027) — закрыта ли
 * камера гостя лично: «Камера после подтверждения» без одобрения ведущего.
 *
 * Два источника, и решает тот, что свежее. Пока гостя нет в составе (зритель,
 * места на сцене не занимал), о нём знает только `/spectate`
 * (`cameraAwaitingApproval`). Как только он в составе, ростер везёт
 * `isCameraBlocked` его строки в `blockedCameras`, и кадр
 * `ParticipantCameraEnabled` («Разрешить камеру») снимает его оттуда сразу — а
 * снимок `/spectate` перечитывается не чаще раза в минуту и отменил бы
 * одобрение до следующего чтения.
 */
export function isGuestCameraBlockedPersonally(input: {
    inBlockedList: boolean;
    inRoster: boolean;
    policy: Pick<GuestPolicy, 'cameraAwaitingApproval'> | null;
}): boolean {
    if (input.inRoster) return input.inBlockedList;
    return input.inBlockedList || input.policy?.cameraAwaitingApproval === true;
}

/**
 * CORE-1745 — слово отозвано: право, прочитанное, пока оно было, больше не
 * действует.
 *
 * `guestCanPublish` в ответе `/spectate` сервер считает ВМЕСТЕ с выданным словом
 * (`CanGuestPublishAsync(party, spectatorId)`), а не только по настройке комнаты.
 * Перечитывает его сторож ростера раз в 60 с, поэтому после отзыва гость ещё до
 * минуты держит `canPublish: true`, гейт выше складывает его с уже погасшим
 * словом — и гость остаётся publisher'ом. Сервер через 8 с проверяет роль в
 * Agora и выбрасывает его (CORE-1550). Прод, комната 837, 18.10.2026: кадр
 * `SpeakingRevoked` дошёл и совпал, гейт ответил `floorGranted: false,
 * guestCanPublish: true, mayPublish: true`, через 9 с — `UID_BANNED`.
 *
 * Поэтому на отзыве право гасится сразу, а экран тут же перечитывает
 * `/spectate`: если комната и без слова пускает гостя говорить, свежий ответ
 * вернёт `true`.
 */
export function guestPolicyAfterFloorRevoked(policy: GuestPolicy | null): GuestPolicy | null {
    return policy && policy.canPublish ? { ...policy, canPublish: false } : policy;
}
